import { useMemo, useState } from 'react'
import { Tags } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import useStore from '@/stores/useStore'
import { readClassLabel } from '@/lib/allocationClasses'

// Bulk class assignment over the Census table's existing selection. This is the
// only place membership is authored: a class is a code on the census row, so
// assigning one is an ordinary census edit rather than a separate plan surface.

// `onAssign` and `onAssignIndividual` come from DataInput so the write goes
// through the same demo-entitlement gate as every other census edit. Calling the
// store directly from here would let a demo user reclass the whole census past
// the modified-row cap.
export default function ClassAssignPopover({ selectedIds, onAssign, onAssignIndividual, onAssigned }) {
  const employees = useStore((state) => state.employees)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')

  const ids = useMemo(() => [...selectedIds], [selectedIds])
  const count = ids.length

  // Codes already in the census, so assigning to an existing class is a click
  // and typos that would silently create a new class are less likely.
  const existing = useMemo(() => {
    const seen = new Map()
    for (const employee of employees) {
      const label = readClassLabel(employee)
      if (label && !seen.has(label.toLowerCase())) seen.set(label.toLowerCase(), label)
    }
    return [...seen.values()]
  }, [employees])

  // The gate returns false when the edit is blocked (demo cap), in which case it
  // has already told the user why and the popover stays put.
  const finish = (applied, message) => {
    if (!applied) return
    setDraft('')
    setOpen(false)
    onAssigned?.(message)
  }

  const assign = (label) => {
    finish(onAssign(ids, label), `${count} ${count === 1 ? 'person' : 'people'} moved to ${label}`)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7">
          <Tags className="mr-1.5 h-3.5 w-3.5" />
          Assign allocation class
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-3">
        <div>
          <p className="text-sm font-medium">Assign {count} selected</p>
          <p className="text-xs text-muted-foreground">
            Allocation classes support class-based contribution formulas.
          </p>
        </div>

        {existing.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Existing classes</p>
            <div className="flex flex-wrap gap-1.5">
              {existing.map((label) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => assign(label)}
                  className="rounded-md border border-input px-2 py-1 text-xs hover:border-primary hover:text-foreground"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        <form
          className="flex gap-1.5"
          onSubmit={(event) => {
            event.preventDefault()
            const label = draft.trim()
            if (label) assign(label)
          }}
        >
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="New class (for example A)"
            aria-label="New allocation class"
            className="h-8"
          />
          <Button type="submit" size="sm" className="h-8 shrink-0" disabled={!draft.trim()}>Set</Button>
        </form>

        <div className="space-y-1.5 border-t border-border pt-2.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-full justify-start px-2 text-xs"
            onClick={() => finish(
              onAssignIndividual(ids),
              `${count} ${count === 1 ? 'employee assigned a unique class' : 'employees assigned unique classes'}`,
            )}
          >
            Give each a unique class
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-full justify-start px-2 text-xs text-muted-foreground"
            onClick={() => finish(
              onAssign(ids, ''),
              `Class cleared for ${count} ${count === 1 ? 'person' : 'people'}`,
            )}
          >
            Clear class (use default)
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
